// React
import { useEffect, useState, useCallback } from 'react'

// Components
import { BaseDialog, FormLabel, FormSelectOption, FormUploadSingleFile, FormTextField } from 'components'

// Interfaces
import { ISharedDocumentUploadProps } from './interfaces'
import { ISharedUploadSupportDocumentForm } from 'interfaces/shared'

// Utils
import { messageUtils_error } from 'utils/common/message.utils'

// i18n
import { useTranslation } from 'react-i18next'

// Lodash
import cloneDeep from 'lodash/cloneDeep'
import pick from 'lodash/pick'

// Antd
import { Col, Form, Row, Spin } from 'antd'

// Utils
import { fileUtils_convertBase64 } from 'utils/common/file.utils'

// Custom Hook
import { useValidation } from 'hooks'
import { commonUtils_handleSelectOptionValue } from 'utils/common/common.utils'

// Mobx
import { observer } from 'mobx-react'

const SharedDocumentUploadCorcomm = observer(
  ({
    isOpen,
    isEdit,
    loading,
    data,
    onClickFirstButton,
    onClickCloseButton,
    onlyUpload,
    attrs,
    fullDetail,
  }: ISharedDocumentUploadProps) => {
    // Hook
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const [isFormLoading, setIsFormLoading] = useState<boolean>(false)
    const { validation_required, validation_validate } = useValidation()

    // Do with the modal
    useEffect(() => {
      if (isOpen) onClear()

      // eslint-disable-next-line
    }, [isOpen])

    // Do when user want to edit
    useEffect(() => {
      if (isEdit && !loading) {
        const fields = form.getFieldsValue()

        form.setFieldsValue({
          ...fields,
          ...pick(data, ['description', 'documentProofId']),
          fileData: '',
          userOriginalFileName: onlyUpload ? '' : data.userOriginalFileName,
          jenisPrasyaratId: commonUtils_handleSelectOptionValue(attrs.documentDetailDropdown, data.jenisPrasyaratId!),
          documentProofId: commonUtils_handleSelectOptionValue(attrs.documentProofDropdown, data.documentProofId!),
        })
      }

      // eslint-disable-next-line
    }, [isEdit, loading])

    /**
     * @description Clear state from current component
     *
     * @return {void} void
     */
    const onClear = (): void => {
      // Reset the forms
      form.resetFields()
    }

    /**
     * @description Watch any change when form is submitted
     *
     * @param {any} form
     *
     * @return {void} void
     */
    const onFinish = (form: ISharedUploadSupportDocumentForm): void => {
      onClickFirstButton(form)
    }

    /**
     * @description Validate form
     *
     * @param {string} jenisPrasyaratId
     * @param {string} documentProofId
     *
     * @return {Promise<void>} Promise<void>
     */
    const onValidateForm = useCallback(
      (jenisPrasyaratId: string, documentProofId: string) => {
        if (attrs?.onValidating && jenisPrasyaratId && documentProofId) {
          return attrs.onValidating({
            jenisPrasyaratId: parseInt(jenisPrasyaratId),
            documentProofId: parseInt(documentProofId),
          })
        }
      },
      // eslint-disable-next-line
      [],
    )

    /**
     * @description Watch any change in form
     *
     * @param {string} type
     * @param {any} value
     *
     * @return {Promise<void>} Promise<void>
     */
    const onChangeForm = async (type: 'documentProofId' | 'jenisPrasyaratId', value: any) => {
      try {
        // Validate Document Detail ID & Document Proof ID
        if (type === 'documentProofId' || type === 'jenisPrasyaratId') {
          if (attrs?.resetValidation) attrs.resetValidation()

          form.setFieldsValue({
            ...form.getFieldsValue(),
            [type]: value,
          })

          if (form.getFieldValue('jenisPrasyaratId') && form.getFieldValue('documentProofId')) {
            await onValidateForm(form.getFieldValue('jenisPrasyaratId'), form.getFieldValue('documentProofId'))
            await form.validateFields(['jenisPrasyaratId', 'documentProofId'])
          }
        }
      } catch (_) {
        //
      }
    }

    /**
     * @description Check before emitting the onClickFirstButton method
     *
     * @return {void} void
     */
    const onBeforeClickFirstButton = async (): Promise<any> => {
      setIsFormLoading(true)

      try {
        // Re validate
        const response = await form.validateFields()
        const clonedResponse = cloneDeep(response)

        onFinish(clonedResponse)
      } catch (err: any) {
        return messageUtils_error(t('app.validations.formNeedValidation'))
      } finally {
        setIsFormLoading(false)
      }
    }

    /**
     * @description Watch any change when user
     *
     */
    const onChangeFile = async (file: File): Promise<void> => {
      try {
        // Make file to base64
        const base64File = await fileUtils_convertBase64(file)

        form.setFieldsValue({
          ...form.getFieldsValue(),
          fileData: base64File,
          userOriginalFileName: file?.name,
        })
      } catch (_) {
        //
      }
    }

    /**
     * @description Render body
     *
     * @return {JSX.Element} JSX.Element
     */
    const renderBody = (): JSX.Element => {
      return (
        <Spin spinning={loading}>
          <Form
            form={form}
            name="my-document-create-edit-form"
            onFinish={onBeforeClickFirstButton}
            scrollToFirstError
            autoComplete="off"
            requiredMark={false}
          >

            {/* Document Proof */}
            <Row className="mb-3">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="myDocument.form.documentProof" />
              </Col>
              <Col xs={24} sm={24} md={9} lg={9} xl={9}>
                {onlyUpload ? (
                  <FormLabel label={data.documentProofName || ''} />
                ) : (
                  <Form.Item
                    name="documentProofId"
                    rules={[
                      validation_required('myDocument.form.documentProof'),
                      {
                        validator: async () => {
                          if (attrs?.isValidating && attrs?.onValidating) {
                            if (attrs?.validation?.isRegistered)
                              return Promise.reject(new Error(t('app.validations.document.documentProofRegistered')))
                          }

                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <FormSelectOption
                      list={attrs.documentProofDropdown}
                      placeholder="myDocument.formPlaceholder.documentProof"
                      disabled={attrs?.isDocumentProofLoading}
                      loading={attrs?.isDocumentProofLoading || attrs?.isValidationLoading}
                      onChange={value => onChangeForm('documentProofId', value)}
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>

            {/* Description */}
            <Row className="mb-3">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="myDocument.form.description" />
              </Col>
              <Col xs={24} sm={24} md={9} lg={9} xl={9}>
                {/* {onlyUpload ? (
                  <FormLabel label={data.jenisPrasyaratName || ''} />
                ) : ( */}
                  <Form.Item
                    name="description"
                    rules={[
                      validation_required('myDocument.form.description'),
                      {
                        validator: async () => {
                          if (attrs?.isValidating && attrs?.onValidating) {
                            if (attrs?.validation?.isRegistered) {
                              return Promise.reject(new Error(t('app.validations.document.documentDetailRegistered')))
                            }
                          }

                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <FormTextField placeholder="myDocument.formPlaceholder.description" />
                  </Form.Item>
                {/* )} */}
              </Col>
            </Row>

            {/* Document */}
            <Row className="mb-3">
              <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                <FormLabel label="myDocument.form.document" />
              </Col>
              <Col xs={24} sm={24} md={9} lg={9} xl={9}>
                <Form.Item noStyle shouldUpdate>
                  {field => {
                    return (
                      <Form.Item
                        name="fileData"
                        rules={validation_validate([!isEdit || onlyUpload ? 'required' : ''], 'myDocument.form.document')}
                      >
                        <FormUploadSingleFile
                          fileName={field.getFieldValue('userOriginalFileName')}
                          onChange={file => onChangeFile(file)}
                          error={field.getFieldError('fileData')?.toString()}
                        />
                      </Form.Item>
                    )
                  }}
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="userOriginalFileName">
              <></>
            </Form.Item>
          </Form>
        </Spin>
      )
    }

    return (
      <BaseDialog
        title={`${`myDocument.${isEdit ? 'update' : 'upload'}Document`}`}
        isOpen={isOpen}
        body={renderBody()}
        firstButtonLabel="app.save"
        onFirstButtonClick={onBeforeClickFirstButton}
        onButtonCloseClick={() => onClickCloseButton(false)}
        isFirstButtonLoading={
          loading || attrs.isSaveLoading || attrs?.isDocumentDetailLoading || isFormLoading || attrs?.isValidationLoading
        }
        closable
        isUsingDefaultFooter
        forceRender
      />
    )
  },
)

export { SharedDocumentUploadCorcomm }
